import { useTranslation } from 'react-i18next';
import { LogOut, ChevronDown } from 'lucide-react';
import { useAuth, type AuthState } from '@/hooks/useAuth';
import { useRoutingUserDetails } from '@/hooks/useRoutingUserDetails';
import type { RoutingUserDetails } from '@/lib/api/routingApi';
import type { UseQueryResult } from '@/lib/query';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { LanguageSubMenu } from '@/components/layout/LanguageSubMenu';

export function ProfileMenu(): React.JSX.Element | null {
  const { t } = useTranslation();
  const { user, logout }: AuthState = useAuth();
  const { data: userDetails }: UseQueryResult<RoutingUserDetails | null> = useRoutingUserDetails();

  if (!user) return null;

  const handleLogout = (): void => {
    void logout();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="flex items-center gap-2 px-3 h-10 hover:bg-accent/50 transition-all"
        >
          <div className="flex flex-col items-end">
            <span className="text-sm font-semibold leading-none text-foreground">{user.name}</span>
            {userDetails && (
              <span className="text-xs text-muted-foreground mt-1">
                {t(`users.roles.${userDetails.role}`)}
              </span>
            )}
          </div>
          <ChevronDown size={14} className="text-muted-foreground shrink-0" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="w-56" align="end" sideOffset={8}>
        <DropdownMenuLabel className="font-normal p-3">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-semibold leading-none">{user.name}</p>
            <p className="text-xs leading-none text-muted-foreground">{user.email}</p>
          </div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        <LanguageSubMenu />

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={handleLogout}
          className="cursor-pointer py-2.5 text-destructive focus:bg-destructive/10 focus:text-destructive"
        >
          <LogOut className="mr-2 h-4 w-4" />
          <span>{t('profile.logout')}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
